import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ConfirmDialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel, AlertDialogDescription,
} from "@/components/ui/alert-dialog";
import { Loader2, Check, X, FileDown, FileText, Inbox, ThumbsUp, ThumbsDown, Eye } from "lucide-react";
import { SCOPE_LABELS } from "./_shared";
import { InlineDiff, DiffLegend } from "@/components/InlineDiff";

const STATUS_TABS = [
  { key: "pending", label: "Oczekujące", icon: Inbox },
  { key: "approved", label: "Zatwierdzone", icon: ThumbsUp },
  { key: "rejected", label: "Odrzucone", icon: ThumbsDown },
];

export default function ProposalsTab() {
  const askConfirm = useConfirm();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState({ count: 0, counts: { pending: 0, approved: 0, rejected: 0 }, items: [] });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await teacherApi.get(`/admin/proposals?status=${tab}&limit=500`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd wczytywania");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab]);

  const approve = async (id) => {
    const ok = await askConfirm({
      title: "Zaakceptować to pytanie?",
      description: "Pytanie trafi do wspólnej puli i będzie widoczne dla wszystkich nauczycieli.",
      confirmText: "Zatwierdź",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await teacherApi.post(`/admin/proposals/${id}/approve`);
      toast.success("Pytanie dodane do wspólnej puli");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd");
    } finally { setBusy(false); }
  };

  const rejectSubmit = async () => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await teacherApi.post(`/admin/proposals/${rejectTarget.id}/reject`, { reason: rejectReason });
      toast.success("Propozycja odrzucona");
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd");
    } finally { setBusy(false); }
  };

  const downloadFile = async (kind) => {
    try {
      const res = await teacherApi.get(`/admin/proposals/export/${kind}?status=${tab}`, { responseType: "blob" });
      const mime = kind === "pdf" ? "application/pdf" : "text/csv";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `propozycje-${tab}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Błąd pobierania ${kind.toUpperCase()}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="proposals-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-black uppercase mb-1">📥 Propozycje pytań</h2>
          <div className="text-sm text-[#64748B]">
            Nauczyciele zgłaszają nowe pytania do wspólnej puli — Ty akceptujesz lub odrzucasz.
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => downloadFile("csv")} disabled={!data.items.length}
                  className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
                  data-testid="proposals-export-csv-btn">
            <FileDown className="w-4 h-4 mr-2" strokeWidth={2.5} />
            CSV
          </Button>
          <Button onClick={() => downloadFile("pdf")} disabled={!data.items.length}
                  className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
                  data-testid="proposals-export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" strokeWidth={2.5} />
            PDF
          </Button>
        </div>
      </div>

      {/* Status tabs */}
      <div className="flex gap-2 flex-wrap">
        {STATUS_TABS.map((s) => {
          const active = tab === s.key;
          const count = data.counts?.[s.key] ?? 0;
          const Icon = s.icon;
          return (
            <button
              key={s.key}
              onClick={() => setTab(s.key)}
              className={`brut ${active ? "bg-[#0F172A] text-white" : "bg-white text-[#0F172A]"}
                          border-2 border-[#0F172A] rounded-md px-4 py-2 font-bold text-sm
                          flex items-center gap-2 transition`}
              data-testid={`proposals-tab-${s.key}`}
            >
              <Icon className="w-4 h-4" strokeWidth={2.5} />
              {s.label}
              <span className={`text-xs px-1.5 rounded ${active ? "bg-white text-[#0F172A]" : "bg-[#0F172A] text-white"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#64748B]" />
        </div>
      ) : !data.items.length ? (
        <div className="brut border-4 border-dashed border-[#94A3B8] rounded-md p-8 text-center text-[#64748B]">
          Brak propozycji w statusie <b>{STATUS_TABS.find((s) => s.key === tab)?.label}</b>.
        </div>
      ) : (
        <div className="space-y-3">
          {data.items.map((p, idx) => (
            <div key={p.id} className="brut border-4 border-[#0F172A] bg-white rounded-md p-4"
                 data-testid={`proposal-${idx}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="brut text-xs tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded-md bg-[#FEF08A]">
                      {SCOPE_LABELS[p.scope] || p.scope}
                    </span>
                    <span className={`brut text-xs uppercase font-bold px-2 py-0.5 rounded-md ${p.type === "mc" ? "bg-[#E0F2FE]" : "bg-[#D1FAE5]"}`}>
                      {p.type === "mc" ? "ABCD" : "Otwarte"}
                    </span>
                    {p.correction_of && (
                      <span className="brut text-xs uppercase font-black px-2 py-0.5 rounded-md bg-[#FEF3C7] text-[#7C2D12] border border-[#B45309]"
                            data-testid={`proposal-correction-badge-${idx}`}>
                        🔧 Poprawka
                      </span>
                    )}
                    {p.topic && <span className="text-xs uppercase font-bold text-[#64748B]">{p.topic}</span>}
                    <span className="text-xs text-[#64748B]">
                      od <b>{p.proposed_by_name || p.proposed_by_email}</b>
                      {p.proposed_at && ` · ${new Date(p.proposed_at).toLocaleString("pl-PL")}`}
                    </span>
                  </div>
                  <div className="font-bold text-base line-clamp-2">{p.text}</div>
                  <div className="text-sm text-[#64748B] mt-1">
                    Odp.: <b>{p.correct}</b>{p.explanation && ` · ${p.explanation.slice(0, 80)}${p.explanation.length > 80 ? "…" : ""}`}
                  </div>
                  {p.correction_of && p.correction_original_snapshot && (
                    <div className="mt-3 border-2 border-[#B45309] rounded-md bg-[#FFFBEB] p-3 space-y-2"
                         data-testid={`proposal-diff-${idx}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="text-xs font-black uppercase tracking-wider text-[#78350F]">
                          🔧 Diff — treść pytania
                        </div>
                        <DiffLegend />
                      </div>
                      <div className="bg-white border border-[#F59E0B] rounded p-2">
                        <InlineDiff
                          before={p.correction_original_snapshot.text || ""}
                          after={p.text || ""}
                          testid={`diff-text-${idx}`}
                        />
                      </div>
                      {p.correction_original_snapshot.correct !== p.correct && (
                        <div className="bg-white border border-[#F59E0B] rounded p-2">
                          <div className="text-[10px] uppercase font-black text-[#78350F] mb-1">
                            Odpowiedź poprawna
                          </div>
                          <InlineDiff
                            before={String(p.correction_original_snapshot.correct || "")}
                            after={String(p.correct || "")}
                            testid={`diff-correct-${idx}`}
                          />
                        </div>
                      )}
                      {p.type === "mc" && p.correction_original_snapshot.type === "mc" && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {(p.options || []).map((opt, oi) => {
                            const oldOpt = (p.correction_original_snapshot.options || [])[oi] || "";
                            if (oldOpt === opt) return null;
                            return (
                              <div key={oi} className="bg-white border border-[#F59E0B] rounded p-2">
                                <span className="font-black text-[#78350F]">{String.fromCharCode(65 + oi)})</span>{" "}
                                <InlineDiff before={oldOpt} after={opt}
                                            testid={`diff-opt-${idx}-${oi}`} />
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                  {p.correction_note && (
                    <div className="text-xs text-[#7C2D12] mt-2 bg-[#FEF3C7] p-2 rounded">
                      <b>Powód poprawki:</b> {p.correction_note}
                    </div>
                  )}
                  {p.status === "rejected" && p.reject_reason && (
                    <div className="text-xs text-[#7F1D1D] mt-2 bg-[#FEE2E2] p-2 rounded">
                      <b>Powód odrzucenia:</b> {p.reject_reason}
                    </div>
                  )}
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button onClick={() => setPreview(p)} variant="ghost" size="sm"
                          className="rounded-md" data-testid={`preview-proposal-${idx}`}>
                    <Eye className="w-4 h-4" strokeWidth={2.5} />
                  </Button>
                  {p.status === "pending" && (
                    <>
                      <Button onClick={() => approve(p.id)} disabled={busy} size="sm"
                              className="brut bg-[#D1FAE5] text-[#065F46] border-2 border-[#0F172A] rounded-md font-bold"
                              data-testid={`approve-proposal-${idx}`}>
                        <Check className="w-4 h-4 mr-1" strokeWidth={3} />
                        Akceptuj
                      </Button>
                      <Button onClick={() => setRejectTarget(p)} disabled={busy} size="sm"
                              className="brut bg-[#FEE2E2] text-[#7F1D1D] border-2 border-[#0F172A] rounded-md font-bold"
                              data-testid={`reject-proposal-${idx}`}>
                        <X className="w-4 h-4 mr-1" strokeWidth={3} />
                        Odrzuć
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview dialog */}
      <AlertDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Podgląd propozycji</AlertDialogTitle>
            {preview && (
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-left">
                  <div><b>Zakres:</b> {SCOPE_LABELS[preview.scope] || preview.scope}</div>
                  <div><b>Temat:</b> {preview.topic || "—"}</div>
                  <div><b>Typ:</b> {preview.type === "mc" ? "ABCD" : "Otwarte"}</div>
                  <div><b>Treść:</b> {preview.text}</div>
                  {preview.type === "mc" && (
                    <ul className="pl-4">
                      {(preview.options || []).map((o, i) => (
                        <li key={i}><b>{String.fromCharCode(65 + i)})</b> {o}</li>
                      ))}
                    </ul>
                  )}
                  <div><b>Odp.:</b> {preview.correct}</div>
                  {preview.explanation && <div><b>Wyjaśnienie:</b> {preview.explanation}</div>}
                </div>
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setPreview(null)}>Zamknij</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject dialog */}
      <AlertDialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReason(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Odrzucić propozycję?</AlertDialogTitle>
            <AlertDialogDescription>
              Podaj powód — trafi do audit-log i będzie widoczny w statusie &bdquo;Odrzucone&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="np. Odpowiedź niepoprawna chemicznie" rows={3}
                    className="border-2 border-[#0F172A] rounded-md"
                    data-testid="reject-reason-textarea" />
          <AlertDialogFooter>
            <AlertDialogCancel>Anuluj</AlertDialogCancel>
            <AlertDialogAction onClick={rejectSubmit} disabled={busy}
                               className="bg-[#EF4444] text-white"
                               data-testid="reject-submit-btn">
              Odrzuć
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
